/**
 * @file ContactRowEditor.tsx
 * @description Строка контакта или соцсети (phase19.16 Р1; SB18, SB58, SB85;
 * uiux-design-r1 §4): вид (у соцсетей — выбор из реестра, Instagram в нём
 * нет), значение с проверкой реестра, флажки «в подвале» и «в меню
 * телефона», «⋯» (Выше, Ниже, Удалить). Почта — только показ: один
 * источник — «Настройки → Магазин».
 */

import { SITE_CONTACT_KINDS, type SiteContactKind } from "@chaiberi/shared";
import { ArrowDown, ArrowUp, Trash2 } from "lucide-react";
import { Link } from "react-router";

import { Input } from "../../ui/input";
import { RowActionsMenu } from "../../ui/RowActionsMenu";
import { SelectField } from "../../ui/SelectField";
import { CheckRow } from "../CheckRow";
import { contactError, type ContactRow } from "../../../lib/site-view/contacts-edit";

interface Props {
  row: ContactRow;
  /** Виды для выбора; один вид — без выбора (подпись). */
  kinds: readonly SiteContactKind[];
  shopEmail: string | null;
  shopSettingsHref: string;
  first: boolean;
  last: boolean;
  onChange: (row: ContactRow) => void;
  onMove: (delta: -1 | 1) => void;
  onRemove: () => void;
}

export function ContactRowEditor({ row, kinds, shopEmail, shopSettingsHref, first, last, onChange, onMove, onRemove }: Props) {
  const info = SITE_CONTACT_KINDS[row.kind];
  const error = contactError(row);
  const inputId = `contact-${row.key}`;

  return (
    <div className="flex flex-col gap-2 rounded-xl bg-surface-lowest p-3 [box-shadow:inset_0_0_0_1px_var(--border)]">
      <div className="flex items-start gap-2">
        <div className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row">
          {kinds.length > 1 ? (
            <SelectField
              ariaLabel="Вид"
              className="sm:w-44"
              value={row.kind}
              options={kinds.map((k) => ({ value: k, label: SITE_CONTACT_KINDS[k].label }))}
              onChange={(kind) => onChange({ ...row, kind: kind as SiteContactKind })}
            />
          ) : (
            <label htmlFor={inputId} className="type-body flex min-h-12 items-center font-medium text-on-surface sm:w-44">
              {info.label}
            </label>
          )}
          {row.kind === "email" ? (
            <p className="type-body flex min-h-12 flex-wrap items-center gap-x-2 text-on-surface">
              {shopEmail ?? "почта не задана"}
              <Link to={shopSettingsHref} className="type-caption font-medium text-primary hover:underline">
                из «Настройки → Магазин» · Изменить →
              </Link>
            </p>
          ) : (
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <Input
                id={inputId}
                aria-label={info.label}
                placeholder={info.placeholder}
                value={row.value ?? ""}
                aria-invalid={!!error && row.value !== ""}
                onChange={(e) => onChange({ ...row, value: e.target.value })}
              />
              {error && row.value !== "" ? <p className="type-caption text-destructive">{error}</p> : null}
            </div>
          )}
        </div>
        <RowActionsMenu
          title={info.label}
          size="compact"
          actions={[
            { key: "up", label: "Выше", icon: ArrowUp, disabled: first, run: () => onMove(-1) },
            { key: "down", label: "Ниже", icon: ArrowDown, disabled: last, run: () => onMove(1) },
            { key: "delete", label: "Удалить", icon: Trash2, danger: true, run: onRemove },
          ]}
        />
      </div>
      <div className="flex flex-wrap gap-x-6">
        <CheckRow checked={row.showInFooter} onChange={(showInFooter) => onChange({ ...row, showInFooter })}>
          В подвале
        </CheckRow>
        <CheckRow checked={row.showInMenu} onChange={(showInMenu) => onChange({ ...row, showInMenu })}>
          В меню телефона
        </CheckRow>
      </div>
    </div>
  );
}
